/**
 * Design-system styling for the upload dialog (see planner-ui/tokens.ts).
 * Kept apart from the page's styles: the dialog's fields and table cells are
 * a size smaller than the catalog's.
 */
import { planner } from "../../planner-ui";

export const panelSx = {
	p: 2,
	bgcolor: planner.surface,
	border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
	borderRadius: `${planner.radius}px`,
	boxShadow: planner.shadowRest,
} as const;

// Neutral count/label chip.
export const chipSx = {
	borderRadius: `${planner.radiusSm}px`,
	bgcolor: planner.surfaceSubtle,
	border: `1px solid ${planner.hairlineStrong}`,
	color: planner.inkSecondary,
	fontFamily: planner.fontUi,
	fontSize: 12,
	fontWeight: 600,
	"& .MuiChip-icon": { color: planner.inkSecondary },
	"& .MuiChip-deleteIcon": {
		color: planner.inkMuted,
		"&:hover": { color: planner.unstaffed },
	},
} as const;

// Machine data chip: SKUs, batch sizes, spec codes, phase codes.
export const chipMonoSx = {
	...chipSx,
	fontFamily: planner.fontMono,
	fontSize: 11,
} as const;

// Status chip built from the staffing semantics (never MUI's palette).
export const statusChipSx = (fill: string, soft: string) => ({
	borderRadius: `${planner.radiusXs}px`,
	bgcolor: soft,
	border: `1px solid ${fill}33`,
	color: fill,
	fontFamily: planner.fontMono,
	fontSize: 11,
	fontWeight: 500,
	letterSpacing: "0.08em",
	textTransform: "uppercase" as const,
});

export const tableHeadCellSx = {
	...planner.labelCaps,
	fontFamily: planner.fontUi,
	color: planner.inkSecondary,
	borderBottomColor: planner.hairline,
} as const;

export const tableCellSx = {
	...planner.type.body,
	fontFamily: planner.fontUi,
	color: planner.ink,
	borderBottomColor: planner.hairline,
} as const;

export const monoTableCellSx = {
	...tableCellSx,
	fontFamily: planner.fontMono,
	fontVariantNumeric: "tabular-nums",
} as const;

export const alertSx = (fill: string, soft: string) => ({
	mb: 2,
	borderRadius: `${planner.radiusSm}px`,
	bgcolor: soft,
	border: `1px solid ${fill}33`,
	color: planner.ink,
	fontFamily: planner.fontUi,
	"& .MuiAlert-icon": { color: fill },
	"& .MuiAlert-message": { ...planner.type.body, fontFamily: planner.fontUi },
});

// Shared control styling: hairline at rest, brick on focus (as on the page).
export const fieldSx = {
	bgcolor: planner.surface,
	"& .MuiOutlinedInput-root": {
		fontFamily: planner.fontUi,
		fontSize: 13,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.inkMuted },
		"&.Mui-focused fieldset": {
			borderColor: planner.accent,
			borderWidth: `${planner.borderWidth}px`,
		},
	},
	"& .MuiInputLabel-root": {
		fontFamily: planner.fontUi,
		fontSize: 13,
		color: planner.inkSecondary,
		"&.Mui-focused": { color: planner.accent },
	},
} as const;

export const menuItemSx = { fontFamily: planner.fontUi, fontSize: 13 } as const;

/** The tables inside a review panel. */
export const tableContainerSx = {
	bgcolor: planner.surface,
	border: `1px solid ${planner.hairline}`,
	borderRadius: `${planner.radiusSm}px`,
	boxShadow: planner.shadowRest,
} as const;
